import clsx from "clsx"
import { FC, useId } from "react"
import style from "./RepairIllustration.module.css"

const TEETH_PATH =
  "M47.1 -19.5L49.9 -10.3L62.4 -8.4L62.4 8.4L49.9 10.3L47.1 19.5L42.6 28.0L50.1 38.2L38.2 50.1L28.0 42.6L19.5 47.1L10.3 49.9L8.4 62.4L-8.4 62.4L-10.3 49.9L-19.5 47.1L-28.0 42.6L-38.2 50.1L-50.1 38.2L-42.6 28.0L-47.1 19.5L-49.9 10.3L-62.4 8.4L-62.4 -8.4L-49.9 -10.3L-47.1 -19.5L-42.6 -28.0L-50.1 -38.2L-38.2 -50.1L-28.0 -42.6L-19.5 -47.1L-10.3 -49.9L-8.4 -62.4L8.4 -62.4L10.3 -49.9L19.5 -47.1L28.0 -42.6L38.2 -50.1L50.1 -38.2L42.6 -28.0ZM35 0A35 35 0 1 0 -35 0A35 35 0 1 0 35 0Z"

type GearProps = {
  isSpinning: boolean
}

// Drawn in SVG rather than using a PNG so that only the teeth turn: the lighting gradients stay fixed on top.
export const Gear: FC<GearProps> = ({ isSpinning }) => {
  // useId() contains colons, which break url(#...) references
  const id = useId().replace(/:/g, "")
  const teeth = `url(#${id}-teeth)`

  return (
    <svg viewBox={"0 0 200 200"}>
      <defs>
        <mask
          id={`${id}-teeth`}
          maskUnits={"userSpaceOnUse"}
          x={-100}
          y={-100}
          width={200}
          height={200}
        >
          <path
            d={TEETH_PATH}
            fill={"#fff"}
            fillRule={"evenodd"}
            stroke={"#fff"}
            strokeWidth={12}
            strokeLinejoin={"round"}
            className={clsx(style["gear-teeth"], isSpinning && style["gear-spinning"])}
          />
        </mask>
        <linearGradient
          id={`${id}-side`}
          gradientUnits={"userSpaceOnUse"}
          x1={-70}
          y1={-70}
          x2={70}
          y2={70}
        >
          <stop
            offset={0}
            stopColor={"#e0703f"}
          />
          <stop
            offset={0.5}
            stopColor={"#c2514f"}
          />
          <stop
            offset={1}
            stopColor={"#8f2f52"}
          />
        </linearGradient>
        <linearGradient
          id={`${id}-face`}
          gradientUnits={"userSpaceOnUse"}
          x1={55}
          y1={-65}
          x2={-50}
          y2={70}
        >
          <stop
            offset={0}
            stopColor={"#fde889"}
          />
          <stop
            offset={0.45}
            stopColor={"#f6d27c"}
          />
          <stop
            offset={0.8}
            stopColor={"#e79a5d"}
          />
          <stop
            offset={1}
            stopColor={"#d17953"}
          />
        </linearGradient>
        <radialGradient
          id={`${id}-shine`}
          gradientUnits={"userSpaceOnUse"}
          cx={20}
          cy={-30}
          r={70}
        >
          <stop
            offset={0}
            stopColor={"#fff"}
            stopOpacity={0.45}
          />
          <stop
            offset={1}
            stopColor={"#fff"}
            stopOpacity={0}
          />
        </radialGradient>
      </defs>
      {/* Two offset copies of the silhouette give the gear its thickness */}
      <g transform={"translate(90 112) scale(.82 1)"}>
        <rect
          x={-100}
          y={-100}
          width={200}
          height={200}
          fill={`url(#${id}-side)`}
          mask={teeth}
        />
      </g>
      <g transform={"translate(95.5 108) scale(.82 1)"}>
        <rect
          x={-100}
          y={-100}
          width={200}
          height={200}
          fill={`url(#${id}-side)`}
          mask={teeth}
        />
      </g>
      <g transform={"translate(101 104) scale(.82 1)"}>
        <rect
          x={-100}
          y={-100}
          width={200}
          height={200}
          fill={`url(#${id}-face)`}
          mask={teeth}
        />
        <rect
          x={-100}
          y={-100}
          width={200}
          height={200}
          fill={`url(#${id}-shine)`}
          mask={teeth}
        />
      </g>
    </svg>
  )
}
